import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ConfigurationProfilesFilters } from '@/components/forms/production/configuration-profiles-filters';
import { ConfigurationProfilesTable } from '@/components/tables/production/configuration-profiles-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useConfigurationProfiles } from '@/hooks/production/use-configuration-profiles';
import type { ConfigurationProfilesFiltersFormData } from '@/schemas/production/configuration-profiles-filters.schema';

export function ConfigurationProfileListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedPage = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const currentPage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;

  const [filters, setFilters] = useState<ConfigurationProfilesFiltersFormData>();

  const { data, isLoading } = useConfigurationProfiles({
    page: currentPage,
    filters,
  });

  const handleApplyFilters = useCallback(
    (newFilters: ConfigurationProfilesFiltersFormData) => {
      setFilters(newFilters);
      setSearchParams((prev) => {
        prev.delete('page');
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setFilters(undefined);
    setSearchParams((prev) => {
      prev.delete('page');
      return prev;
    });
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        prev.set('page', String(page));
        return prev;
      });
    },
    [setSearchParams],
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Perfis de Configuração</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie todos os perfis de configuração registrados
          </p>
        </div>
        <Button asChild className="bg-black hover:bg-gray-800 text-white">
          <Link to="/production/configuration-profile/form/create">
            <Plus className="mr-2 h-4 w-4" />
            Novo Perfil
          </Link>
        </Button>
      </div>

      <Separator />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <ConfigurationProfilesFilters
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lista de Perfis</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ConfigurationProfilesTable
            data={data}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
}
